import type { AxiosError } from 'axios'

import {
  ErrorType,
  ErrorLevel,
  type ProcessedError,
  type ErrorHandlerConfig,
  type ErrorContext,
  isAxiosError,
  isNetworkError,
  isTimeoutError
} from './errorTypes'
import type { ApiResponse } from './types'

import { useUIStore } from '@/stores/useUIStore'

export class ErrorHandler {
  private static config: ErrorHandlerConfig = {
    enableLogging: true,
    enableUserNotification: true,
    enableErrorReporting: false,
    defaultRetryOptions: {
      maxRetries: 3,
      delay: 1000
    }
  }

  // 配置错误处理器
  static configure(config: Partial<ErrorHandlerConfig>): void {
    this.config = { ...this.config, ...config }
  }

  // 主要错误处理入口
  static async handleError(
    error: unknown,
    context?: Partial<ErrorContext>
  ): Promise<ProcessedError> {
    const processedError = this.processError(error, context)
    
    // 记录错误日志
    if (this.config.enableLogging) {
      this.logError(processedError)
    }

    // 显示用户通知
    if (this.config.enableUserNotification) {
      this.showUserNotification(processedError)
    }

    // 错误上报
    if (this.config.enableErrorReporting) {
      await this.reportError(processedError)
    }

    return processedError
  }

  // 错误分类和标准化处理
  private static processError(
    error: unknown,
    context?: Partial<ErrorContext>
  ): ProcessedError {
    const timestamp = Date.now()
    const baseContext: ErrorContext = {
      timestamp,
      ...context
    }

    // Axios 错误处理
    if (isAxiosError(error)) {
      return this.processAxiosError(error, baseContext)
    }

    // JavaScript 原生错误
    if (error instanceof Error) {
      return this.processJavaScriptError(error, baseContext)
    }

    // 未知错误
    return this.processUnknownError(error, baseContext)
  }

  // 处理 Axios 错误
  private static processAxiosError(
    error: AxiosError<ApiResponse>,
    context: ErrorContext
  ): ProcessedError {
    const { response, config } = error
    const status = response?.status
    const errorContext = {
      ...context,
      url: config?.url,
      method: config?.method?.toUpperCase()
    }

    // 网络错误
    if (isNetworkError(error)) {
      return {
        type: ErrorType.NETWORK,
        level: ErrorLevel.HIGH,
        code: 'NETWORK_ERROR',
        message: '网络连接失败',
        userMessage: '网络连接异常，请检查网络设置后重试',
        context: errorContext.url,
        details: error,
        timestamp: context.timestamp,
        canRetry: true,
        retryOptions: this.config.defaultRetryOptions
      }
    }

    // 超时错误
    if (isTimeoutError(error)) {
      return {
        type: ErrorType.TIMEOUT,
        level: ErrorLevel.MEDIUM,
        code: 'TIMEOUT_ERROR',
        message: '请求超时',
        userMessage: '请求超时，请稍后重试',
        context: errorContext.url,
        details: error,
        timestamp: context.timestamp,
        canRetry: true,
        retryOptions: this.config.defaultRetryOptions
      }
    }

    // HTTP 状态码错误
    if (status) {
      return this.processHttpError(status, response.data, errorContext)
    }

    // 其他 Axios 错误
    return {
      type: ErrorType.UNKNOWN,
      level: ErrorLevel.MEDIUM,
      code: 'AXIOS_ERROR',
      message: error.message || '请求异常',
      userMessage: '请求处理异常，请稍后重试',
      context: errorContext.url,
      details: error,
      timestamp: context.timestamp,
      canRetry: true
    }
  }

  // 处理 HTTP 状态码错误
  private static processHttpError(
    status: number,
    responseData: ApiResponse | undefined,
    context: ErrorContext
  ): ProcessedError {
    const timestamp = context.timestamp

    switch (true) {
      case status === 400:
        return {
          type: ErrorType.VALIDATION,
          level: ErrorLevel.LOW,
          code: 'VALIDATION_ERROR',
          message: responseData?.message || '请求参数错误',
          userMessage: '请求参数有误，请检查输入内容',
          context: context.url,
          details: responseData,
          timestamp,
          canRetry: false
        }

      case status === 401:
        return {
          type: ErrorType.AUTH,
          level: ErrorLevel.HIGH,
          code: 'UNAUTHORIZED',
          message: '未授权访问',
          userMessage: '登录已过期，请重新登录',
          context: context.url,
          details: responseData,
          timestamp,
          canRetry: false
        }

      case status === 403:
        return {
          type: ErrorType.PERMISSION,
          level: ErrorLevel.MEDIUM,
          code: 'FORBIDDEN',
          message: '权限不足',
          userMessage: '您没有权限执行此操作',
          context: context.url,
          details: responseData,
          timestamp,
          canRetry: false
        }

      case status === 404:
        return {
          type: ErrorType.BUSINESS,
          level: ErrorLevel.LOW,
          code: 'NOT_FOUND',
          message: '资源不存在',
          userMessage: '请求的资源不存在',
          context: context.url,
          details: responseData,
          timestamp,
          canRetry: false
        }

      case status >= 500:
        return {
          type: ErrorType.SERVER,
          level: ErrorLevel.CRITICAL,
          code: 'SERVER_ERROR',
          message: responseData?.message || '服务器内部错误',
          userMessage: '服务器繁忙，请稍后重试',
          context: context.url,
          details: responseData,
          timestamp,
          canRetry: true,
          retryOptions: this.config.defaultRetryOptions
        }

      default:
        return {
          type: ErrorType.UNKNOWN,
          level: ErrorLevel.MEDIUM,
          code: `HTTP_${status}`,
          message: responseData?.message || `HTTP ${status} 错误`,
          userMessage: '请求处理异常，请稍后重试',
          context: context.url,
          details: responseData,
          timestamp,
          canRetry: true
        }
    }
  }

  // 处理 JavaScript 原生错误
  private static processJavaScriptError(
    error: Error,
    context: ErrorContext
  ): ProcessedError {
    return {
      type: ErrorType.UNKNOWN,
      level: ErrorLevel.MEDIUM,
      code: 'JS_ERROR',
      message: error.message || '程序执行异常',
      userMessage: '系统异常，请刷新页面后重试',
      context: error.stack,
      details: error,
      timestamp: context.timestamp,
      canRetry: true
    }
  }

  // 处理未知错误
  private static processUnknownError(
    error: unknown,
    context: ErrorContext
  ): ProcessedError {
    return {
      type: ErrorType.UNKNOWN,
      level: ErrorLevel.MEDIUM,
      code: 'UNKNOWN_ERROR',
      message: '未知错误',
      userMessage: '系统异常，请稍后重试',
      context: typeof error === 'string' ? error : undefined,
      details: error,
      timestamp: context.timestamp,
      canRetry: true
    }
  }

  // 记录错误日志
  private static logError(error: ProcessedError): void {
    const logMethod = this.getLogMethod(error.level)

    logMethod(`[${error.type.toUpperCase()}] ${error.code}: ${error.message}`, {
      level: error.level,
      context: error.context,
      timestamp: new Date(error.timestamp).toISOString(),
      canRetry: error.canRetry,
      details: error.details
    })
  }

  // 根据错误级别选择日志方法
  private static getLogMethod(level: ErrorLevel): typeof console.log {
    switch (level) {
      case ErrorLevel.LOW:
        return console.info
      case ErrorLevel.MEDIUM:
        return console.warn
      case ErrorLevel.HIGH:
      case ErrorLevel.CRITICAL:
        return console.error
      default:
        return console.log
    }
  }

  // 显示用户通知
  private static showUserNotification(error: ProcessedError): void {
    console.log(`[UI通知] ${error.level.toUpperCase()}: ${error.userMessage}`)
    // 根据错误级别显示不同类型的通知
    switch (error.level) {
      case ErrorLevel.CRITICAL:
      case ErrorLevel.HIGH:
        ErrorHandler.showError(error.userMessage)
        break
      case ErrorLevel.MEDIUM:
        ErrorHandler.showWarning(error.userMessage)
        break
      case ErrorLevel.LOW:
      default:
        ErrorHandler.showInfo(error.userMessage)
        break
    }
  }

  // 错误上报（可扩展集成监控系统）
  private static async reportError(error: ProcessedError): Promise<void> {
    try {
      // 这里可以集成错误监控系统，比如 Sentry、LogRocket 等
      console.log('错误上报:', {
        type: error.type,
        code: error.code,
        message: error.message,
        context: error.context,
        timestamp: error.timestamp,
        level: error.level
      })
      
      // 示例：上报到监控系统
      // await fetch('/api/error-report', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify({
      //     type: error.type,
      //     code: error.code,
      //     message: error.message,
      //     context: error.context,
      //     timestamp: error.timestamp,
      //     level: error.level,
      //     userAgent: navigator.userAgent,
      //     url: window.location.href
      //   })
      // })
    } catch (reportError) {
      console.error('错误上报失败:', reportError)
    }
  }

  // 重试机制
  static async retryRequest<T>(
    requestFn: () => Promise<T>,
    retryOptions = this.config.defaultRetryOptions
  ): Promise<T> {
    let lastError: unknown
    
    for (let attempt = 0; attempt <= retryOptions!.maxRetries; attempt++) {
      try {
        return await requestFn()
      } catch (error) {
        lastError = error
        
        if (attempt < retryOptions!.maxRetries) {
          await this.delay(retryOptions!.delay * Math.pow(2, attempt)) // 指数退避
        }
      }
    }
    
    throw lastError
  }

  // 延迟工具函数
  private static delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  // 公开方法：直接显示通知（供外部调用）
  static showNotification(
    type: 'success' | 'error' | 'info' | 'warning',
    message: string,
    autoHide?: boolean
  ): void {
    const uiStore = useUIStore.getState()
    uiStore.showMessage(type, message, autoHide)
  }

  // 公开方法：设置加载状态
  static setLoading(isLoading: boolean, text?: string): void {
    const uiStore = useUIStore.getState()
    uiStore.setLoading(isLoading, text)
  }

  // 便捷方法：显示成功消息
  static showSuccess(message: string): void {
    const uiStore = useUIStore.getState()
    uiStore.showSuccess(message)
  }

  // 便捷方法：显示错误消息
  static showError(message: string): void {
    const uiStore = useUIStore.getState()
    uiStore.showError(message)
  }

  // 便捷方法：显示警告消息
  static showWarning(message: string): void {
    const uiStore = useUIStore.getState()
    uiStore.showWarning(message)
  }

  // 便捷方法：显示信息消息
  static showInfo(message: string): void {
    const uiStore = useUIStore.getState()
    uiStore.showInfo(message)
  }
}